import { notification } from 'antd';
import { FC, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { formatDate } from 'src/utils/date-util';
import { observeChannels } from 'src/utils/firebase/firebase-short-cut';
import { onForegroundFcmMessage } from 'src/utils/firebase/firebase-messaging';
import { playNotificationSound } from 'src/utils/notifications/browserNotification';

import { ChatPushPayloadData, fromChatPushPayload } from 'src/redux/models/chatNotificationsModel';
import { resetChatNotifications, setInboxUnreadCount } from 'src/redux/slices/chatNotifications';
import { useAppDispatch } from 'src/redux/store';

import { useAuth } from 'src/stores/auth-context';

const CHAT_ROUTE = '/channel/chat';

/** 글로벌 고객 채팅 메시지 리스너 (UI 없음). admin 권한 사용자에게만 마운트된다. */
const ChatNotificationListener: FC = () => {
  const { user } = useAuth();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const role = user?.role;
  const isOps = role === 'admin';

  const locationRef = useRef(location);
  locationRef.current = location;

  // foreground FCM onMessage: payload 기반으로 토스트/사운드 처리 (실시간 알림).
  useEffect(() => {
    if (!isOps) return;

    const unsubscribe = onForegroundFcmMessage((payload) => {
      const data = (payload.data ?? {}) as ChatPushPayloadData;
      const item = fromChatPushPayload(data, {
        now: formatDate(new Date(), "yyyy-MM-dd'T'HH:mm:ss+0900"),
      });
      if (!item) return;

      const currentSearch = new URLSearchParams(locationRef.current.search);
      const isViewingThisChannel =
        locationRef.current.pathname === CHAT_ROUTE && currentSearch.get('channelId') === item.channelId;
      if (isViewingThisChannel) return;

      const title = `새 고객 메시지${item.title ? ` - ${item.title}` : ''}`;
      const body = item.body ?? '(이미지 또는 첨부)';
      const navigateToChat = () => navigate(`${CHAT_ROUTE}?channelId=${item.channelId}`);

      notification.info({
        message: title,
        description: body,
        onClick: navigateToChat,
        placement: 'topRight',
      });

      playNotificationSound();
    });

    return unsubscribe;
  }, [isOps, navigate]);

  // background SW에서 notificationclick 시 postMessage(CHAT_NAVIGATE)로 라우팅 요청.
  useEffect(() => {
    if (!isOps) return;
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

    const handler = (event: MessageEvent) => {
      const data = event.data as { type?: string; channelId?: string } | null;
      if (!data || data.type !== 'CHAT_NAVIGATE' || !data.channelId) return;
      navigate(`${CHAT_ROUTE}?channelId=${data.channelId}`);
    };

    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [isOps, navigate]);

  // 전역 받은편지함 안읽음 카운트 — consoleInbox.adminUnread === true 채널 수를 배지로 노출.
  // useChannelInbox는 채팅 페이지 한정이므로 배지용 전역 스코프 리스너가 따로 필요하다.
  useEffect(() => {
    if (!isOps) return;
    const subscription = observeChannels([['consoleInbox.adminUnread', '==', true]]).subscribe({
      next: (channels) => dispatch(setInboxUnreadCount(channels.length)),
    });
    return () => {
      subscription.unsubscribe();
      dispatch(resetChatNotifications());
    };
  }, [isOps, dispatch]);

  return null;
};

export default ChatNotificationListener;
